import docCookies from 'mozilla-doc-cookies';
import { getGeolocationEndpoint, readGeolocationRegion } from './geolocation-service.mjs';

const {
	geolocation_environment,
	geolocation_tooltip_text,
	geolocation_tooltip_label,
	geolocation_close_label,
	provinces,
} = window.aftermetooData;

const provinceCodeNames = {
	ab: provinces['ab'],
	bc: provinces['bc'],
	mb: provinces['mb'],
	nb: provinces['nb'],
	nl: provinces['nl'],
	ns: provinces['ns'],
	nt: provinces['nt'],
	nu: provinces['nu'],
	on: provinces['on'],
	pe: provinces['pe'],
	qc: provinces['qc'],
	sk: provinces['sk'],
	yt: provinces['yt'],
	federal: provinces['federal'],
};
const provinceCodes = Object.keys(provinceCodeNames);

// `regionKey` drives redirects (the Cloudflare worker and the theme resolve a regional
// article's parent with it) and always holds one of `provinceCodes`. `geoKey` holds the
// visitor's real location as reported by the worker, unnormalized.
const regionKey = 'WP_AFTERMETOO_REGION';
const geoKey = 'WP_AFTERMETOO_GEOLOCATION_REGION';

/** Show the detected jurisdiction in the relaunch header (no navigation, no filtering). */
function showRelaunchRegion(regionCode) {
	const selector = document.querySelector('[data-region-selector]');
	if (!selector || !provinceCodes.includes(regionCode)) return;
	const code = regionCode.toUpperCase();
	const label = selector.querySelector('[data-region-label]');
	const summary = selector.querySelector('[data-region-summary]');
	if (label) label.textContent = code;
	if (summary) summary.setAttribute('aria-label', `${summary.dataset.labelPrefix || ''}: ${code}`);
	selector.querySelectorAll('[data-region-option]').forEach((choice) => {
		if (choice.dataset.regionOption === regionCode) choice.setAttribute('aria-current', 'true');
		else choice.removeAttribute('aria-current');
	});
}

/**
 * On a regional article for another jurisdiction than the visitor's location, offer the
 * article for their own. `data-guide-region` is the article's jurisdiction and
 * `data-guide-region-alternatives` maps every jurisdiction to its sibling's permalink.
 */
function showRegionNotice(geoRegion) {
	const regionEl = document.querySelector('[data-guide-region]');
	if (!regionEl || !geoRegion || document.querySelector('.geolocation-tip')) return;

	const guideRegion = (regionEl.dataset.guideRegion || '').toLowerCase().trim();
	const visitorRegion = geoRegion.toLowerCase().trim();
	let alternatives = {};
	try {
		alternatives = JSON.parse(regionEl.dataset.guideRegionAlternatives || '{}') || {};
	} catch (error) {
		return;
	}
	const target = alternatives[visitorRegion];
	if (!guideRegion || guideRegion === visitorRegion || typeof target !== 'string') return;

	let href;
	try {
		const url = new URL(target, window.location.href);
		if (url.origin !== window.location.origin) return;
		href = url.href;
	} catch (error) {
		return;
	}

	const link = document.createElement('a');
	link.href = href;
	const linkStart = '\u0001';
	const linkEnd = '\u0002';
	// The template is a translated plain-text sentence with entity-encoded quotes; decode
	// it, then build the DOM so no value is ever parsed as HTML.
	const decoder = document.createElement('textarea');
	decoder.innerHTML = geolocation_tooltip_text || '';
	const sentence = Object.entries({
		'{{CURRENT_REGION}}': provinceCodeNames[guideRegion] || guideRegion.toUpperCase(),
		'{{GEOLOCATED_REGION}}': provinceCodeNames[visitorRegion] || visitorRegion.toUpperCase(),
		'{{OPEN_LINK_TAG}}': linkStart,
		'{{CLOSE_LINK_TAG}}': linkEnd,
	}).reduce((text, [search, replace]) => text.split(search).join(replace), decoder.value);

	const text = document.createElement('p');
	const [before, rest = ''] = sentence.split(linkStart);
	const [inside, after = ''] = rest.split(linkEnd);
	text.append(before);
	if (inside) {
		link.textContent = inside;
		text.append(link);
	}
	text.append(after);

	const close = document.createElement('button');
	close.type = 'button';
	close.className = 'geolocation-tip__close';
	close.setAttribute('aria-label', geolocation_close_label || 'Close');
	close.addEventListener('click', () => notice.remove());

	const notice = document.createElement('div');
	notice.setAttribute('role', 'status');
	const relaunchAnchor = document.querySelector('.amt-article-breadcrumb');
	if (relaunchAnchor) {
		notice.className = 'amt-geo-notice geolocation-tip';
		const label = document.createElement('span');
		label.className = 'amt-geo-notice__label';
		label.textContent = geolocation_tooltip_label || '';
		notice.append(label, text, close);
		relaunchAnchor.insertAdjacentElement('afterend', notice);
		return;
	}

	// Legacy templates keep their original tip callout.
	notice.className = 'wp-block-vs-callout is-style-tip-block geolocation-tip';
	const tip = document.createElement('div');
	tip.className = 'wp-block-vs-callout-tip';
	const tipLabel = document.createElement('span');
	tipLabel.className = 'callout-tip';
	tipLabel.textContent = geolocation_tooltip_label || '';
	tip.append(tipLabel);
	notice.append(tip, text, close);
	const mainEl = document.querySelector('#main');
	if (mainEl) mainEl.insertBefore(notice, mainEl.firstChild);
}

window.addEventListener('DOMContentLoaded', () => {
	const dropdownLabel = document.querySelector('.nav-utility .dropdown .link');
	const currentEnv = getGeolocationEndpoint(geolocation_environment, document);

	let regionCookie = docCookies.getItem(regionKey);
	let geoCookie = docCookies.getItem(geoKey);

	// Either cookie missing: ask the Cloudflare worker where the visitor is.
	if (currentEnv && (regionCookie === null || geoCookie === null)) {
		const xmlhttp = new XMLHttpRequest();

		xmlhttp.onreadystatechange = function () {
			if (xmlhttp.readyState !== XMLHttpRequest.DONE) return;
			const regionCode = readGeolocationRegion(xmlhttp);
			if (!regionCode) return;
			const normalizedRegionCode = provinceCodes.includes(regionCode) ? regionCode : 'on';
			// Session cookies, and only where unset: a manual selection made before or
			// during the request always wins over the automatic location guess.
			if (null === docCookies.getItem(regionKey)) {
				docCookies.setItem(regionKey, normalizedRegionCode, null, '/', null, true);
			}
			if (null === docCookies.getItem(geoKey)) {
				docCookies.setItem(geoKey, regionCode, null, '/', null, true);
			}

			const effectiveRegion = docCookies.getItem(regionKey) || normalizedRegionCode;
			const provBtn = document.getElementById('province-selector');
			if (provBtn) {
				provBtn.classList.remove('opacity-toggle');
				const provCode = effectiveRegion.toUpperCase();
				provBtn.textContent = provCode;
				// WCAG 2.5.3 Label in Name: keep the accessible name in step with the text.
				provBtn.setAttribute('aria-label', (provBtn.dataset.labelPrefix || 'Province Selector') + ': ' + provCode);
			}
			showRelaunchRegion(effectiveRegion);
			showRegionNotice(docCookies.getItem(geoKey));
		};

		xmlhttp.open('GET', currentEnv, true);
		xmlhttp.timeout = 4000;
		xmlhttp.send();
	}

	regionCookie = docCookies.getItem(regionKey);
	geoCookie = docCookies.getItem(geoKey);

	if (regionCookie) {
		const provBtn = document.getElementById('province-selector');
		if (provBtn) {
			provBtn.classList.remove('opacity-toggle');
			provBtn.textContent = regionCookie.toUpperCase();
		}
		// The page cache serves every visitor the same HTML, so the server-rendered
		// header label is the default; show the visitor's own jurisdiction instead.
		showRelaunchRegion(regionCookie.toLowerCase());
	}

	// A returning visitor already carries the location: show the notice right away.
	showRegionNotice(geoCookie);

	// Legacy header region menu.
	const regions = document.querySelectorAll('.nav-utility .region-menu [data-region]');
	regions.forEach((region) => {
		region.addEventListener('click', function (event) {
			const regionCode = this.dataset.region;
			event.preventDefault();
			if (dropdownLabel) {
				dropdownLabel.textContent = regionCode.toUpperCase();
				dropdownLabel.setAttribute('aria-label', regionCode);
			}
			// A manual choice is remembered (no expiry) and overrides the location guess.
			docCookies.setItem(regionKey, regionCode.toLowerCase(), Infinity, '/', null, true);

			const dataRegions = JSON.parse(
				document.querySelector('[data-guide-region-alternatives]')?.dataset?.guideRegionAlternatives || false
			);
			if (false !== dataRegions && dataRegions[regionCode.toLowerCase()]) {
				window.location = dataRegions[regionCode.toLowerCase()];
			}
		});
	});

	// Support Organization filters (legacy): preselect the visitor's province.
	const provFilter = document.querySelector('input#current-province');
	if (provFilter !== null && regionCookie !== null && regionCookie !== 'federal') {
		const url = new URL(window.location);
		if (url.searchParams.get('province') === null) {
			url.searchParams.set('province', regionCookie);
			window.location = url.href;
		}
	}
});
